/**
 * Sticky ATC — slides up once the in-form buy controls pass under the header.
 * Reads the live variant from the product form (picker + hidden id), POSTs
 * /cart/add.js, then dispatches `product_atc_via_form` so the quick cart opens.
 * Figma node has no motion data; timing matches announcement-bar (400ms).
 */
(() => {
  const TAG = 'sticky-atc';

  if (customElements.get(TAG)) return;

  const ROOT = window.Shopify?.routes?.root || '/';

  const COMPARE_TRAY_EVENT = 'product-compare:tray-visibility';
  const COMPARE_TRAY_CLASS = 'has-compare-tray';

  // LiveChat is an app embed, so it can finish loading after this module.
  const CHAT_POLL_INTERVAL = 500;
  const CHAT_POLL_ATTEMPTS = 20;
  const enqueueCartMutation = (() => {
    if (window.themeEnqueueCartMutation) return window.themeEnqueueCartMutation;

    let queue = Promise.resolve();
    window.themeEnqueueCartMutation = (mutation) => {
      const next = queue.then(mutation, mutation);
      queue = next.catch(() => {});
      return next;
    };

    return window.themeEnqueueCartMutation;
  })();

  class StickyAtc extends HTMLElement {
    #observer = null;
    #form = null;
    #productForm = null;
    #resizeFrame = 0;
    #chatPoll = 0;
    #pastControls = false;
    #compareTrayVisible = false;

    connectedCallback() {
      this.#form =
        document.getElementById(this.dataset.formId || '') ||
        this.closest('.product')?.querySelector('.product-form__form') ||
        document.querySelector('product-form .product-form__form');

      this.#productForm =
        this.#form?.closest('product-form') ||
        this.closest('.product')?.querySelector('product-form') ||
        document.querySelector('product-form');

      this.#submitButton()?.addEventListener('click', this.#onSubmitClick);
      this.#productForm?.addEventListener('variant:change', this.#onVariantChange);
      this.#productForm?.addEventListener('product-price:update', this.#onPriceUpdate);
      window.addEventListener('resize', this.#onResize);
      document.addEventListener(COMPARE_TRAY_EVENT, this.#onCompareTray);

      // The tray can already be up when this upgrades, and it only emits on a
      // change, so the root class is the starting point.
      this.#compareTrayVisible =
        document.documentElement.classList.contains(COMPARE_TRAY_CLASS);

      this.#syncFromLiveState();
      this.#observe();
      this.#watchChat();
    }

    disconnectedCallback() {
      this.#submitButton()?.removeEventListener('click', this.#onSubmitClick);
      this.#productForm?.removeEventListener(
        'variant:change',
        this.#onVariantChange
      );
      this.#productForm?.removeEventListener(
        'product-price:update',
        this.#onPriceUpdate
      );
      window.removeEventListener('resize', this.#onResize);
      document.removeEventListener(COMPARE_TRAY_EVENT, this.#onCompareTray);
      window.cancelAnimationFrame(this.#resizeFrame);
      window.clearInterval(this.#chatPoll);
      window.LiveChatWidget?.off?.('visibility_changed', this.#onChatVisibility);
      this.#observer?.disconnect();
      this.#observer = null;
      document.documentElement.classList.remove('has-sticky-atc');
      document.documentElement.classList.remove('has-chat-expanded');
    }

    /**
     * The chat launcher rides above the bar, but an expanded chat can fill the
     * viewport and has nowhere to move to, so the lift is dropped while it is
     * open.
     */
    #watchChat() {
      const subscribe = () => {
        const widget = window.LiveChatWidget;

        if (typeof widget?.on !== 'function') return false;

        widget.on('visibility_changed', this.#onChatVisibility);

        // `get` throws until the widget is ready; the event covers that case.
        try {
          this.#onChatVisibility(widget.get('state'));
        } catch {
          /* not ready */
        }

        return true;
      };

      if (subscribe()) return;

      let attempts = 0;

      this.#chatPoll = window.setInterval(() => {
        attempts += 1;

        if (subscribe() || attempts >= CHAT_POLL_ATTEMPTS) {
          window.clearInterval(this.#chatPoll);
          this.#chatPoll = 0;
        }
      }, CHAT_POLL_INTERVAL);
    }

    #onChatVisibility = (state) => {
      document.documentElement.classList.toggle(
        'has-chat-expanded',
        state?.visibility === 'maximized'
      );
    };

    #submitButton() {
      return this.querySelector('.sticky-atc__submit');
    }

    #picker() {
      return this.#productForm?.querySelector('product-variant-picker');
    }

    #unselectedOption() {
      const options = this.#picker()?.querySelectorAll('[data-option]') || [];

      for (const option of options) {
        const selected = option.querySelector(
          '[data-option-value][aria-checked="true"]'
        );

        if (!selected) return option;
      }

      return null;
    }

    #firstOptionRoot() {
      return this.#picker()?.querySelector('[data-option]') || null;
    }

    #currentVariant() {
      return (
        this.#productForm?.getCurrentVariant?.() ||
        this.#picker()?.getCurrentVariant?.() ||
        null
      );
    }

    #sentinel() {
      return this.#productForm?.querySelector('[data-product-form-controls]');
    }

    #syncFromLiveState() {
      const variant = this.#currentVariant();

      if (variant) {
        this.#syncPriceFromVariant(variant);
        this.#syncButton(variant);
        return;
      }

      this.#syncPriceFromDom();
      this.#syncButtonFromInForm();
    }

    /**
     * Bottom edge of the sticky header. The in-form ATC is still usable while
     * it sits under that line, so it is the viewport top as far as the bar is
     * concerned. A header that scrolls away with the page contributes nothing.
     */
    #stickyHeaderInset() {
      const header = document.querySelector('.header-section');

      if (!(header instanceof HTMLElement)) return 0;

      const styles = window.getComputedStyle(header);

      if (styles.position !== 'sticky' && styles.position !== 'fixed') return 0;

      const top = Number.parseFloat(styles.top);
      const offset = Number.isFinite(top) ? Math.max(0, top) : 0;

      return Math.round(offset + header.offsetHeight);
    }

    #onResize = () => {
      window.cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = window.requestAnimationFrame(() => this.#observe());
    };

    #observe() {
      const sentinel = this.#sentinel();

      this.#observer?.disconnect();
      this.#observer = null;

      if (!sentinel) {
        this.#setVisible(false);
        return;
      }

      // Header height changes with the breakpoint, so the root is re-inset on
      // resize rather than measured once.
      const inset = this.#stickyHeaderInset();

      this.#observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          if (!entry) return;

          const rect = entry.boundingClientRect;

          // The bar stands in for the in-form controls whenever none of them
          // are on screen: scrolled up behind the header, or still below the
          // fold on the way down the page.
          const hasLayout = rect.width > 0 || rect.height > 0;

          this.#setVisible(hasLayout && !entry.isIntersecting);
        },
        { threshold: 0, rootMargin: `-${inset}px 0px 0px 0px` }
      );

      this.#observer.observe(sentinel);
    }

    #onCompareTray = (event) => {
      const visible = Boolean(event.detail?.visible);

      if (visible === this.#compareTrayVisible) return;

      this.#compareTrayVisible = visible;
      this.#applyVisibility();
    };

    #setVisible(visible) {
      this.#pastControls = visible;
      this.#applyVisibility();
    }

    /**
     * The compare tray takes the same bottom slot, so it wins the space while
     * it is up and the bar drops back to its own rule once the tray goes.
     */
    #applyVisibility() {
      const visible = this.#pastControls && !this.#compareTrayVisible;

      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');

      if (visible) {
        this.removeAttribute('inert');
        document.documentElement.classList.add('has-sticky-atc');
        document.documentElement.style.setProperty(
          '--sticky-atc-offset',
          `${this.offsetHeight + this.#bottomOffset()}px`
        );
      } else {
        this.setAttribute('inert', '');
        document.documentElement.classList.remove('has-sticky-atc');
        document.documentElement.style.removeProperty('--sticky-atc-offset');
      }
    }

    #bottomOffset() {
      const styles = window.getComputedStyle(this);
      const bottom = Number.parseFloat(styles.bottom);

      return Number.isFinite(bottom) ? bottom : 0;
    }

    #setBusy(busy) {
      const button = this.#submitButton();

      if (!(button instanceof HTMLButtonElement)) return;

      button.disabled = busy;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
      button.classList.toggle('sticky-atc__submit--loading', busy);
    }

    #flashError() {
      const button = this.#submitButton();

      if (!(button instanceof HTMLElement)) return;

      button.classList.add('sticky-atc__submit--error');
      window.setTimeout(() => {
        button.classList.remove('sticky-atc__submit--error');
      }, 1600);
    }

    #announce(message) {
      const status = this.querySelector('[data-sticky-atc-status]');

      if (!message || !(status instanceof HTMLElement)) return;

      status.textContent = '';
      window.requestAnimationFrame(() => {
        status.textContent = message;
      });
    }

    #announceInventoryError() {
      this.#announce(this.dataset.inventoryError);
      this.#flashError();
    }

    #announceQuantityRuleError(message) {
      this.#announce(message);
      this.#flashError();
    }

    #clampQuantityToLimits() {
      const selector = this.#form?.querySelector('quantity-selector');
      const limits = window.themeQuantityLimitsForVariant?.(this.#currentVariant());

      if (selector && typeof selector.setLimits === 'function' && limits) {
        selector.setLimits(limits);
      } else if (selector && typeof selector.setMax === 'function' && limits?.max != null) {
        selector.setMax(limits.max);
      }
    }

    #promptForOption(option) {
      const template = this.dataset.selectOptionError || '';
      const name = option?.getAttribute('data-option-name') || '';

      this.#announce(template.replace('__option__', name));
      this.#focusOption(option);
      this.#flashError();
    }

    #promptForUnavailable() {
      this.#announce(this.dataset.unavailableError);
      this.#focusOption(this.#firstOptionRoot());
      this.#flashError();
    }

    #focusOption(option) {
      if (!(option instanceof HTMLElement)) return;

      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      option.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'center',
      });

      const firstValue = option.querySelector(
        '[data-option-value]:not([disabled])'
      );

      if (firstValue instanceof HTMLElement) {
        firstValue.focus({ preventScroll: true });
      }
    }

    #clearStatus() {
      const status = this.querySelector('[data-sticky-atc-status]');

      if (status instanceof HTMLElement) {
        status.textContent = '';
      }
    }

    #onSubmitClick = (event) => {
      event.preventDefault();

      const button = this.#submitButton();

      if (!(button instanceof HTMLButtonElement) || button.disabled) return;
      if (!this.#form) return;

      const unselected = this.#unselectedOption();

      if (unselected) {
        this.#promptForOption(unselected);
        return;
      }

      const variant = this.#currentVariant();

      if (this.#isNotifyVariant(variant)) {
        return;
      }

      // Products without options have no picker, so there is nothing to
      // validate and the form's variant id is already correct.
      if (this.#picker() && (!variant || variant.available === false)) {
        this.#promptForUnavailable();
        return;
      }

      const formData = new FormData(this.#form);
      const id = formData.get('id');
      const quantity = Number(formData.get('quantity') || '1') || 1;
      const sellingPlan = Number(formData.get('selling_plan') || 0);
      const selector = this.#form.querySelector('quantity-selector');
      const ruleError = selector?.getRuleError?.();

      if (!id) return;

      if (ruleError) {
        this.#clampQuantityToLimits();
        this.#announceQuantityRuleError(ruleError);
        return;
      }

      const limits = window.themeQuantityLimitsForVariant?.(variant);

      if (limits?.max != null && quantity > limits.max) {
        this.#clampQuantityToLimits();
        this.#announceInventoryError();
        return;
      }

      this.#setBusy(true);

      this.#addToCart(id, quantity, sellingPlan)
        .then((data) => {
          document.dispatchEvent(
            new CustomEvent('product_atc_via_form', {
              bubbles: true,
              detail: { items: data.items || data, source: 'sticky-atc' },
            })
          );
        })
        .catch((error) => {
          if (error?.code === 'quantity_rule') {
            this.#clampQuantityToLimits();
            this.#announceQuantityRuleError(error.message);
            return;
          }

          if (error?.code === 'inventory') {
            this.#clampQuantityToLimits();
            this.#announceInventoryError();
            return;
          }

          this.#flashError();
        })
        .finally(() => {
          this.#setBusy(false);
          this.#syncFromLiveState();
        });
    };

    async #addToCart(id, quantity, sellingPlan) {
      const paid = Number(id);
      const gift = Number(this.#productForm?.getAttribute('data-free-gift-variant-id') || 0);
      const item =
        sellingPlan > 0
          ? { id: paid, quantity, selling_plan: sellingPlan }
          : { id: paid, quantity };
      const items = [item];

      if (gift > 0 && gift !== paid && !window.themeFreeGift?.isPromotionSuppressed?.(String(gift))) {
        items.push({
          id: gift,
          quantity: 1,
          properties: {
            _gwp: 'yes',
            _gwp_promotion: String(gift),
          },
        });
      }

      const postItems = (payloadItems) =>
        enqueueCartMutation(() =>
          fetch(`${ROOT}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items: payloadItems }),
          }),
        );

      let response = await postItems(items);
      let data = await response.json().catch(() => ({}));

      if (!response.ok && items.length > 1) {
        response = await postItems([item]);
        data = await response.json().catch(() => ({}));
      }

      if (!response.ok) {
        // A rejected add can still put part of the quantity in the cart (up to a
        // stock or basket limit), so ask quick cart and the header count to catch up.
        document.dispatchEvent(new CustomEvent('cart_refresh'));

        const message = data.description || data.message || 'Unable to add to cart';
        const error = new Error(message);

        if (/stock|inventor|sold out|available/i.test(message)) {
          error.code = 'inventory';
        } else if (/minimum|maximum|increment|quantity/i.test(message)) {
          error.code = 'quantity_rule';
        }

        throw error;
      }

      return data;
    }

    #onVariantChange = (event) => {
      const variant = event.detail?.variant || this.#currentVariant();

      if (!variant) return;

      this.#clearStatus();
      this.#syncPriceFromVariant(variant);
      this.#syncButton(variant);
    };

    /* B2B volume pricing rewrites the in-form price as quantity crosses a break. */
    #onPriceUpdate = () => {
      this.#syncPriceFromDom();
    };

    #formQuantity() {
      const value = Number(
        this.#form?.querySelector('[data-quantity-input]')?.value
      );

      return Number.isFinite(value) && value > 0 ? Math.floor(value) : 1;
    }

    /** B2B catalog breaks make the unit price depend on the chosen quantity. */
    #unitPrice(variant) {
      const price = window.themeUnitPriceForQuantity?.(
        variant,
        this.#formQuantity()
      );

      return Number.isFinite(price) ? price : variant.price;
    }

    #syncPriceFromVariant(variant) {
      const stickyPrice = this.querySelector('[data-sticky-atc-price]');
      const stickyCompareRoot = this.querySelector('[data-sticky-atc-compare]');
      const stickyCompare = this.querySelector('[data-sticky-atc-compare-value]');
      const formatMoney = this.#moneyFormatter();
      const price = this.#unitPrice(variant);

      if (stickyPrice) {
        stickyPrice.textContent = formatMoney(price);
      }

      if (stickyCompareRoot && stickyCompare) {
        const showCompare =
          variant.compare_at_price != null && variant.compare_at_price > price;

        stickyCompareRoot.hidden = !showCompare;

        if (showCompare) {
          stickyCompare.textContent = formatMoney(variant.compare_at_price);
        }
      }
    }

    #syncPriceFromDom() {
      const priceRoot = this.#productForm?.querySelector('[data-product-price]');

      if (!priceRoot) return;

      const livePrice = priceRoot.querySelector('[data-current-price-value]');
      const liveCompareRoot = priceRoot.querySelector('[data-compare-price]');
      const liveCompare = priceRoot.querySelector('[data-compare-price-value]');
      const stickyPrice = this.querySelector('[data-sticky-atc-price]');
      const stickyCompareRoot = this.querySelector('[data-sticky-atc-compare]');
      const stickyCompare = this.querySelector('[data-sticky-atc-compare-value]');

      if (stickyPrice && livePrice) {
        stickyPrice.textContent = livePrice.textContent;
      }

      if (stickyCompareRoot && liveCompareRoot) {
        const showCompare = !liveCompareRoot.hidden;

        stickyCompareRoot.hidden = !showCompare;

        if (showCompare && stickyCompare && liveCompare) {
          stickyCompare.textContent = liveCompare.textContent;
        }
      }
    }

    #isNotifyVariant(variant) {
      if (!variant) return false;

      const tracked = Boolean(variant.inventory_management);
      const continueSelling = variant.inventory_policy === 'continue';

      return (
        tracked && !continueSelling && variant.available === false
      );
    }

    #syncButton(variant) {
      const button = this.#submitButton();

      if (!(button instanceof HTMLButtonElement)) return;

      const available =
        variant.available !== false &&
        window.themeHasPurchasableQuantity?.(variant) !== false;
      const notify = this.#isNotifyVariant(variant);
      const labelEl = button.querySelector('.button__label');
      const addLabel = this.dataset.addLabel || '';
      const soldOutLabel = this.dataset.soldOutLabel || '';
      const notifyLabel = this.dataset.notifyLabel || soldOutLabel;

      this.classList.toggle('sticky-atc--notify', notify);
      button.classList.toggle('sticky-atc__notify', notify);
      button.disabled = !available && !notify;
      button.classList.toggle('button--disabled', !available && !notify);
      button.setAttribute('aria-disabled', available || notify ? 'false' : 'true');

      if (notify) {
        button.setAttribute('data-back-in-stock-trigger', '');
        button.setAttribute('aria-haspopup', 'dialog');
      } else {
        button.removeAttribute('data-back-in-stock-trigger');
        button.removeAttribute('aria-haspopup');
      }

      if (labelEl) {
        if (notify && notifyLabel) {
          labelEl.textContent = notifyLabel;
        } else if (!available && soldOutLabel) {
          labelEl.textContent = soldOutLabel;
        } else if (available && addLabel) {
          labelEl.textContent = addLabel;
        }
      }
    }

    #syncButtonFromInForm() {
      const root = this.#form?.querySelector('.buy-buttons');
      const inForm = root?.querySelector('.buy-buttons__submit');

      if (!(inForm instanceof HTMLButtonElement)) return;

      if (root.classList.contains('buy-buttons--notify')) {
        this.#syncButton({
          available: false,
          inventory_management: 'shopify',
          inventory_policy: 'deny',
        });
        return;
      }

      this.#syncButton({ available: !inForm.disabled });
    }

    #moneyFormatter() {
      const priceRoot = this.#productForm?.querySelector('[data-product-price]');

      if (window.Shopify?.formatMoney) {
        return (cents) => window.Shopify.formatMoney(cents);
      }

      const format = priceRoot?.dataset?.moneyFormat;

      if (format) {
        return (cents) => {
          const value = (Number(cents) / 100).toFixed(2);
          const withSeparators = value.replace(/\d(?=(\d{3})+\.)/g, '$&,');

          return format
            .replace(/\{\{\s*amount\s*\}\}/, withSeparators)
            .replace(
              /\{\{\s*amount_no_decimals\s*\}\}/,
              String(Math.round(Number(cents) / 100))
            )
            .replace(
              /\{\{\s*amount_with_comma_separator\s*\}\}/,
              value.replace('.', ',')
            )
            .replace(
              /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/,
              String(Math.round(Number(cents) / 100))
            );
        };
      }

      const sample =
        priceRoot?.querySelector('[data-current-price-value]')?.textContent?.trim() ||
        this.querySelector('[data-sticky-atc-price]')?.textContent?.trim() ||
        '';
      const currencyMatch = sample.match(/[^\d\s.,-]+/);
      const currency = currencyMatch ? currencyMatch[0] : '';
      const currencyFirst = Boolean(currency && sample.startsWith(currency));

      return (cents) => {
        const value = (Number(cents) / 100).toFixed(2);

        if (!currency) return value;

        return currencyFirst ? `${currency}${value}` : `${value}${currency}`;
      };
    }
  }

  customElements.define(TAG, StickyAtc);
})();
